import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { safeFetch } from "@/api/safeFetch";
import { Input, ButtonSiguiente } from "@/components/ui";

type ProfesionalCaptado = {
  profesional_id: number;
  nombre_empresa?: string;
  nombre_comercial?: string;
  codigo?: string;
  porcentaje_descuento?: number;
  puntos?: number;
  puntos_por_suscriptor?: number;
  // Puntos generados específicamente por esta captación
  puntos_ganados?: number;
  categoria?: "bronce" | "plata" | "oro" | "diamante";
  aplicado_en: string;
};

export default function PanelAfiliados() {
  const [captados, setCaptados] = useState<ProfesionalCaptado[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [month, setMonth] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const [scrollY, setScrollY] = useState(0);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // ======================
  // PARALLAX SCROLL
  // ======================
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ======================
  // CARGA DATOS
  // ======================
  const cargarDatos = async () => {
    setLoading(true);
    setError("");

    try {
      let url = "/api/afiliados/captados";

      if (month) {
        url += `?month=${month}`;
      } else if (fechaInicio && fechaFin) {
        const start = `${fechaInicio}T00:00:00Z`;
        const end = `${fechaFin}T23:59:59Z`;

        url += `?start=${start}&end=${end}`;
      }

      console.log("Consultando:", url);

      const res = await safeFetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      console.log("Respuesta captados:", res.status, data);

      if (!res.ok) {
        console.error("Error backend:", data);

        throw new Error(
          data?.message || `Error cargando datos (${res.status})`,
        );
      }

      setCaptados(data.resultados || []);
    } catch (err) {
      console.error("Error cargando captaciones:", err);

      setError(err instanceof Error ? err.message : "Error desconocido");

      setCaptados([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPuntos = captados.length > 0 ? Number(captados[0].puntos ?? 0) : 0;

  useEffect(() => {
    cargarDatos();
  }, []);

  const getCategoriaStyle = (categoria?: string) => {
    switch (categoria?.toLowerCase()) {
      case "bronce":
        return {
          icono: "🥉",
          nombre: "Bronce",
          className: "bg-orange-500/20 text-orange-300",
        };

      case "plata":
        return {
          icono: "🥈",
          nombre: "Plata",
          className: "bg-slate-300/20 text-slate-200",
        };

      case "oro":
        return {
          icono: "🥇",
          nombre: "Oro",
          className: "bg-yellow-400/20 text-yellow-300",
        };

      case "diamante":
        return {
          icono: "💎",
          nombre: "Diamante",
          className: "bg-cyan-400/20 text-cyan-300",
        };

      default:
        return {
          icono: "🎁",
          nombre: "Bronce",
          className: "bg-orange-500/20 text-orange-300",
        };
    }
  };

  return (
    <div className="w-full min-h-screen flex justify-center p-4 relative overflow-hidden text-white">
      {/* 🌄 BACKGROUND IMAGE PARALLAX */}
      <div
        className="absolute inset-0 bg-cover bg-center scale-150"
        style={{
          backgroundImage: "url('/img/futuristaLinkServs.png')",
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      />

      {/* 🌫️ DARK OVERLAY */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

      {/* 🌊 WATER WAVES ANIMATION */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="wave" />
        <div className="wave wave2" />
      </div>

      {/* 🔷 MAIN PANEL */}
      <div className="relative z-10 w-full max-w-5xl flex flex-col gap-6">
        {/* BACK BUTTON */}
        <div className="absolute top-2 left-0 z-50">
          <ButtonSiguiente
            onClick={() => navigate("/")}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 px-2"
          >
            ⬅ Volver
          </ButtonSiguiente>
        </div>

        {/* TITLE */}
        <h1 className="text-3xl font-bold text-center drop-shadow-lg">
          Mis Puntos
        </h1>

        {/* FILTERS GLASS */}
        <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl p-4 sm:p-5 shadow-2xl w-full">
          <div className="flex flex-col md:grid md:grid-cols-4 gap-4 w-full">
            {/* MES */}
            <div className="w-full max-w-full overflow-hidden">
              <label className="text-xs block mb-1">Mes</label>

              <div className="w-full max-w-full">
                <Input
                  type="month"
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    setFechaInicio("");
                    setFechaFin("");
                  }}
                  className="!w-full !max-w-full dark:text-zinc-700 [&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
            </div>

            {/* DESDE */}
            <div className="w-full max-w-full overflow-hidden">
              <label className="text-xs block mb-1">Desde</label>

              <div className="w-full max-w-full">
                <Input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => {
                    setFechaInicio(e.target.value);
                    setMonth("");
                  }}
                  className="!w-full !max-w-full dark:text-zinc-700 [&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
            </div>

            {/* HASTA */}
            <div className="w-full max-w-full overflow-hidden">
              <label className="text-xs block mb-1">Hasta</label>

              <div className="w-full max-w-full">
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => {
                    setFechaFin(e.target.value);
                    setMonth("");
                  }}
                  className="!w-full !max-w-full dark:text-zinc-700 [&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
            </div>

            {/* BOTÓN */}
            <div className="w-full max-w-full">
              <ButtonSiguiente
                onClick={cargarDatos}
                disabled={loading}
                className="!w-full bg-emerald-500 hover:bg-emerald-400 text-white"
              >
                {loading ? "Cargando..." : "Filtrar"}
              </ButtonSiguiente>
            </div>
          </div>
        </div>

        {/* ERROR */}
        {error && <p className="text-red-400 text-center">{error}</p>}

        {/* CAPTACIONES */}
        <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl p-4 shadow-xl">
          {loading && <p className="text-center py-6">Cargando...</p>}

          {!loading && captados.length === 0 && (
            <p className="text-center text-white/70 py-6">No hay captaciones</p>
          )}

          {!loading && captados.length > 0 && (
            <>
              {/* ========================= */}
              {/* 💻 TABLA ESCRITORIO */}
              {/* ========================= */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="text-left border-b border-white/20">
                      <th className="p-3">Empresa</th>
                      <th className="p-3">Código utilizado</th>
                      <th className="p-3">Descuento</th>
                      <th className="p-3 text-center">Puntos obtenidos</th>
                      <th className="p-3">Fecha</th>
                    </tr>
                  </thead>

                  <tbody>
                    {captados.map((p, index) => {
                      const categoria = getCategoriaStyle(p.categoria);
                      const tienePuntos = Number(p.puntos_ganados ?? 0) > 0;

                      return (
                        <tr
                          key={`${p.profesional_id}-${p.aplicado_en}-${index}`}
                          className="border-b border-white/10 hover:bg-white/10 transition"
                        >
                          {/* EMPRESA */}
                          <td className="p-3 font-medium">
                            {p.nombre_empresa ||
                              p.nombre_comercial ||
                              "Empresa"}
                          </td>

                          {/* CÓDIGO */}
                          <td className="p-3">
                            {p.codigo ? (
                              <span className="rounded-lg bg-blue-500/20 px-3 py-1 text-blue-200 font-semibold tracking-wider">
                                {p.codigo}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>

                          {/* DESCUENTO */}
                          <td className="p-3">
                            <span className="rounded-full bg-amber-400/20 px-3 py-1 text-amber-300 font-bold">
                              {p.porcentaje_descuento ?? 0}%
                            </span>
                          </td>

                          {/* PUNTOS */}
                          <td className="p-3 text-center">
                            {tienePuntos ? (
                              <span
                                className={`rounded-full px-3 py-1 font-bold ${categoria.className}`}
                              >
                                {categoria.icono} +{p.puntos_ganados} puntos
                              </span>
                            ) : (
                              <span className="rounded-full bg-white/10 px-3 py-1 text-white/60 font-medium">
                                🎁 Sin puntos
                              </span>
                            )}
                          </td>

                          {/* FECHA */}
                          <td className="p-3 text-white/80 whitespace-nowrap">
                            {new Date(p.aplicado_en).toLocaleString("es-ES", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ========================= */}
              {/* 📱 TARJETAS MÓVIL */}
              {/* ========================= */}
              <div className="md:hidden flex flex-col gap-4">
                {captados.map((p, index) => {
                  const categoria = getCategoriaStyle(p.categoria);

                  const tienePuntos = Number(p.puntos_ganados ?? 0) > 0;

                  return (
                    <div
                      key={`${p.profesional_id}-${p.aplicado_en}-${index}`}
                      className="rounded-2xl border border-white/15 bg-black/20 p-4 shadow-lg"
                    >
                      {/* EMPRESA */}
                      <div className="mb-4 border-b border-white/10 pb-3">
                        <p className="text-xs text-white/50">Empresa</p>

                        <p className="text-base font-bold">
                          {p.nombre_empresa || p.nombre_comercial || "Empresa"}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {/* CÓDIGO */}
                        <div>
                          <p className="mb-1 text-xs text-white/50">
                            Código utilizado
                          </p>

                          {p.codigo ? (
                            <span className="inline-block rounded-lg bg-blue-500/20 px-2 py-1 text-blue-200 font-semibold tracking-wider">
                              {p.codigo}
                            </span>
                          ) : (
                            <span className="text-white/50">—</span>
                          )}
                        </div>

                        {/* DESCUENTO */}
                        <div>
                          <p className="mb-1 text-xs text-white/50">
                            Descuento
                          </p>

                          <span className="inline-block rounded-full bg-amber-400/20 px-3 py-1 text-amber-300 font-bold">
                            {p.porcentaje_descuento ?? 0}%
                          </span>
                        </div>

                        {/* PUNTOS */}
                        <div className="col-span-2">
                          <p className="mb-2 text-xs text-white/50">
                            Puntos obtenidos
                          </p>

                          {tienePuntos ? (
                            <span
                              className={`inline-block rounded-full px-3 py-1 font-bold ${categoria.className}`}
                            >
                              {categoria.icono} +{p.puntos_ganados} puntos
                            </span>
                          ) : (
                            <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-white/60 font-medium">
                              🎁 Sin puntos
                            </span>
                          )}
                        </div>

                        {/* FECHA */}
                        <div className="col-span-2 border-t border-white/10 pt-3">
                          <p className="mb-1 text-xs text-white/50">
                            Fecha de captación
                          </p>

                          <p className="text-sm text-white/80">
                            📅{" "}
                            {new Date(p.aplicado_en).toLocaleString("es-ES", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* TOTAL Y LEYENDA */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* LEYENDA CATEGORÍAS - IZQUIERDA */}
          <div className="flex flex-wrap justify-center md:justify-start gap-2 text-xs">
            <span className="rounded-full bg-orange-500/20 px-3 py-1 text-orange-300 font-medium">
              🥉 Bronce: 10 puntos
            </span>

            <span className="rounded-full bg-slate-300/20 px-3 py-1 text-slate-200 font-medium">
              🥈 Plata: 15 puntos
            </span>

            <span className="rounded-full bg-yellow-400/20 px-3 py-1 text-yellow-300 font-medium">
              🥇 Oro: 20 puntos
            </span>

            <span className="rounded-full bg-cyan-400/20 px-3 py-1 text-cyan-300 font-medium">
              💎 Diamante: 25 puntos
            </span>
          </div>

          {/* TOTAL PUNTOS - DERECHA */}
          <span className="whitespace-nowrap rounded-full bg-yellow-400/20 px-4 py-2 text-yellow-300 font-bold">
            ⭐ {totalPuntos} puntos
          </span>
        </div>
      </div>

      {/* 🌊 CSS WAVES */}
      <style>{`
        .wave {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 200%;
          height: 400px;
          background: rgba(255,255,255,0.08);
          border-radius: 100%;
          animation: wave 12s infinite linear;
        }

        .wave2 {
          animation-delay: -6s;
          opacity: 0;
        }

        @keyframes wave {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(-50%) translateY(20px); }
        }
      `}</style>
    </div>
  );
}
